import React, { useCallback, useContext } from 'react';
import {
  Button,
  Link,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
  shorthands,
} from '@fluentui/react-components';
import { MessageBar, MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-message-bar';
import { LightbulbFilamentFilled, WarningFilled } from '@fluentui/react-icons';
import { I18nContext } from '@bingads-webui-react/i18n-context';

import { DiagnosticsTypes } from './constants';
import { DiagnosticBarProps } from './types';

const useDiagnosticsBarStyles = makeStyles({
  messageBar: {
    marginTop: '8px',
    '&.recommendation-bar': {
      backgroundColor: tokens.colorBrandBackground2,
      ...shorthands.borderColor(tokens.colorBrandBackground2Pressed),
    },
  },
  messageBarBody: {
    whiteSpace: 'normal',
    paddingTop: '8px',
    paddingBottom: '8px',
    '& > span': {
      marginRight: '8px',
    },
  },
  messageBarAction: {
    alignSelf: 'center',
  },
  iconIssue: {
    color: tokens.colorStatusWarningForeground1,
  },
  iconRecommendation: {
    color: tokens.colorNeutralForeground2BrandHover,
  },
});

export function DiagnosticBar({ itemList, type, onActionClick }: DiagnosticBarProps) {
  const classes = useDiagnosticsBarStyles();
  const i18n = useContext(I18nContext) as any;

  const renderActions = useCallback((item) => {
    if (!item.actions) return null;
    const actionButtons = item.actions.map((action) => {
      const onButtonClick = () => onActionClick({ action, item });
      return (
        <Button className="action-button" size="small" onClick={onButtonClick}>
          <Text weight="bold">
            {i18n.getString(action.text)}
          </Text>
        </Button>
      );
    });
    return actionButtons;
  }, [onActionClick, i18n]);

  return (
    <>
      {itemList.map(item => (
        <MessageBar
          className={mergeClasses(classes.messageBar, type === DiagnosticsTypes.Recommendation ? 'recommendation-bar' : 'issue-bar', 'message-bar', item.locator)}
          icon={
            type === DiagnosticsTypes.Recommendation
              ? <LightbulbFilamentFilled className={classes.iconRecommendation} />
              : <WarningFilled className={classes.iconIssue} />
          }
          intent={type === DiagnosticsTypes.Recommendation ? 'info' : 'warning'}
          layout="singleline"
        >
          <MessageBarBody className={mergeClasses(classes.messageBarBody, 'message-body')}>
            <MessageBarTitle>{i18n.getString(item.title)}</MessageBarTitle>
            <span>{i18n.getString(item.description)}</span>
            {item.link && <Link href={item.link.url} target="blank">{i18n.getString(item.link.text)}</Link>}
          </MessageBarBody>
          <MessageBarActions className={classes.messageBarAction}>
            {renderActions(item)}
          </MessageBarActions>
        </MessageBar>
      ))}
    </>
  );
}

DiagnosticBar.defaultProps = {
  itemList: [],
  type: DiagnosticsTypes.Issue,
};
